import { useContext, useState, useEffect } from "react";
import "./login.scss";
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber,
  linkWithCredential,
} from "firebase/auth";
import { auth, database } from "../../firebase";
import { ref, get, update } from "firebase/database";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import WGLogoLink from "../../Images/HealthLogo.png";
import bcrypt from "bcryptjs";
import { updatePassword } from "firebase/auth";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import LoadingSpinner from "../../components/widget/LoadingSpinner";

const Login = () => {
  const [error, setError] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationError, setVerificationError] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailError, setResetEmailError] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
    useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState("");
  const [phoneVerificationError, setPhoneVerificationError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(false);
  const [timer, setTimer] = useState(30);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [showPasswordUpdateModal, setShowPasswordUpdateModal] = useState(false);
  const [password2, setPassword2] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error2, setError2] = useState("");
  const [success, setSuccess] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  const [phoneNumber, setPhoneNumber] = useState(null); // Add state for storing phone number

  const [isPhoneNumberUpdated, setIsPhoneNumberUpdated] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Start loading immediately
    setLoginLoading(true);

    try {
      // Attempt to sign in with email and password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Fetch the user's data from Realtime Database
      const userRef = ref(database, `users/${user.uid}`);
      const userSnapshot = await get(userRef);

      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        let userPhoneNumber = userData.phoneNumber;

        // Ensure phone number format consistency
        if (!userPhoneNumber.startsWith("+")) {
          userPhoneNumber = `+${userPhoneNumber}`;
        }

        setPhoneNumber(userPhoneNumber);

        const userRole = userData.role;
        const displayName = userData.displayName || "No Name";
        const companyId = userData.companyId;
        const companyName = userData.companyName;
        const locationId = userData.locationId;
        const locationName = userData.locationName;

        // ✅ Set isPhoneNumberUpdated and reset flag in DB
        if (userData.phoneNumUpdated === true) {
          setIsPhoneNumberUpdated(true);
   
        }

        // Prevent login for users with role 5
        if (userRole === 5) {
          setShowMobileAppModal(true);
          await signOut(auth);
          return;
        }

        // Check if phone number matches Firebase Authentication
        let phoneNumberVerified = false;
        if (user.phoneNumber) {
          const formattedPhoneNumber = user.phoneNumber.startsWith("+")
            ? user.phoneNumber
            : `+${user.phoneNumber}`;
          phoneNumberVerified = formattedPhoneNumber === userPhoneNumber;
        }

        if (!phoneNumberVerified) {
          setShowPhoneVerificationModal(true);
        } else if (
          userData.firstTimeLogIn === true ||
          userData.firstTimeLogIn === undefined
        ) {
          setShowPasswordUpdateModal(true);
        } else {
          // Dispatch login action
          dispatch({
            type: "LOGIN",
            payload: {
              uid: user.uid,
              email: user.email,
              displayName: displayName,
              role: userRole,
              companyId: companyId,
              companyName: companyName,
              locationId: locationId,
              locationName: locationName,
            },
          });

          navigate("/"); // Navigate to home page
        }
      } else {
        console.error("No user data found in Realtime Database!");
      }
    } catch (err) {
      console.error("Login error: ", err);

      if (err.code === "auth/too-many-requests") {
        alert("Too many login attempts. Please try again later.");
      } else {
        setError(true);
      }
    } finally {
      // Keep loading for at least 2 seconds
      setTimeout(() => {
        setLoginLoading(false);
      }, 1000);
    }
  };

  // Modal to display QR Codes for App Store and Google Play
  const [showMobileAppModal, setShowMobileAppModal] = useState(false);

  const handleCloseModal = () => setShowMobileAppModal(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password2 !== confirmPassword) {
      setError2("Passwords do not match.");
      return;
    }

    if (
      !/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,20}$/.test(
        password2
      )
    ) {
      setError2("Password does not meet the requirements.");
      return;
    }

    setLoading(true);
    try {
      // Encrypt password before updating
      console.log("Before hashing:", password2); // Log password before hashing
      const hashedPassword = await bcrypt.hash(password2, 10); // Hash password with bcrypt
      console.log("Hashed password:", hashedPassword); // Log the hashed password

      // Update password in Firebase Authentication (using plain password)
      await updatePassword(auth.currentUser, password2); // Firebase will encrypt it internally

      // Save hashed password in the Firebase Realtime Database
      const userRef = ref(database, `users/${auth.currentUser.uid}`);
      await update(userRef, {
        password: hashedPassword, // Save hashed password to DB
        firstTimeLogIn: false,
      });

      setSuccess(true);
      setShowPasswordUpdateModal(false); // Close modal after success
      alert(
        "Your password has been successfully updated. Please log in again using your new password."
      );
      setPassword("");
    } catch (error) {
      setError2("Error updating password: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // const handleResendEmail = async () => {
  //   if (resendCooldown) return; // Prevent multiple clicks during cooldown

  //   try {
  //     const userCredential = await signInWithEmailAndPassword(
  //       auth,
  //       email,
  //       password
  //     );
  //     const user = userCredential.user;

  //     if (!user.emailVerified) {
  //       // Resend the email verification if the user's email is not verified
  //       await sendEmailVerification(user);
  //       alert("Verification email sent!");

  //       // Start cooldown timer
  //       setResendCooldown(true);
  //       setTimer(30);
  //       const countdown = setInterval(() => {
  //         setTimer((prev) => {
  //           if (prev === 1) {
  //             clearInterval(countdown);
  //             setResendCooldown(false);
  //           }
  //           return prev - 1;
  //         });
  //       }, 1000);
  //     }
  //   } catch (error) {
  //     console.error("Error sending verification email:", error);
  //     alert("There was an issue sending the verification email.");
  //   }
  // };

  //newpasswordmodal

  // Check if user needs to update password
  useEffect(() => {
    if (showPasswordUpdateModal && auth.currentUser) {
      const userRef = ref(database, `users/${auth.currentUser.uid}`);
      get(userRef).then((snapshot) => {
        const userData = snapshot.val();
        if (
          userData &&
          (userData.firstTimeLogIn === true ||
            userData.firstTimeLogIn === undefined)
        ) {
          setShowPasswordUpdateModal(true);
        }
      });
    }
  }, [showPasswordUpdateModal]);

  const handleResendPhoneVerification = async () => {
    setLoginLoading(true);
    if (resendCooldown || isSendingVerification) return;
    setIsSendingVerification(true); // Set loading state

    let userPhoneNumber = phoneNumber;
    if (!userPhoneNumber.startsWith("+")) {
      userPhoneNumber = `+${userPhoneNumber}`;
    }

    try {
      // Ensure reCAPTCHA container exists
      if (!document.getElementById("recaptcha-container")) {
        const recaptchaDiv = document.createElement("div");
        recaptchaDiv.id = "recaptcha-container";
        document.body.appendChild(recaptchaDiv);
      }

      // Reset and create new reCAPTCHA verifier if needed
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
            callback: (response) => {
              console.log("reCAPTCHA verified:", response);
            },
          }
        );
      } else {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
            callback: (response) => {
              console.log("reCAPTCHA verified:", response);
            },
          }
        );
      }

      await window.recaptchaVerifier.render(); // Ensure reCAPTCHA is rendered

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        userPhoneNumber,
        window.recaptchaVerifier
      );

      window.confirmationResult = confirmationResult;
      alert("Verification code sent!");
      setVerificationCodeSent(true);

      // Start cooldown timer
      setResendCooldown(true);
      setTimer(30);
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(countdown);
            setResendCooldown(false);
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error sending phone verification code:", error);
      alert(
        "There was an issue sending the verification code. Please contact your admin to make sure the phone number is valid."
      );
    } finally {
      setIsSendingVerification(false);
      setLoginLoading(false);
    }
  };

  const handlePhoneVerification = async () => {
    if (!phoneVerificationCode) {
      setPhoneVerificationError("Please enter the verification code.");
      return;
    }

    if (!window.confirmationResult) {
      setPhoneVerificationError("Phone verification has not been initiated.");
      return;
    }

    try {
      const confirmationResult = window.confirmationResult;
      const credential = PhoneAuthProvider.credential(
        confirmationResult.verificationId,
        phoneVerificationCode
      );

      const user = auth.currentUser;
      if (user) {
        await updatePhoneNumber(user, credential);
        console.log("Phone number updated successfully in Firebase Auth.");

           // ✅ Reset phoneNumUpdated flag in Realtime Database
      await update(ref(database, `users/${user.uid}`), {
        phoneNumUpdated: false,
      });

        alert("Phone number verified successfully!");
        setShowPhoneVerificationModal(false); // Close modal on success
        handleLogin();
      }
    } catch (error) {
      if (error.code === "auth/account-exists-with-different-credential") {
        console.error("Phone number already linked to a different account.");

        // Fetch existing sign-in methods for the account
        const email = error.customData?.email; // Get email from error data (if available)
        if (email) {
          const existingMethods = await fetchSignInMethodsForEmail(auth, email);
          console.log("Existing sign-in methods:", existingMethods);

          alert(
            `This phone number is linked to an existing account. Please sign in using: ${existingMethods.join(
              ", "
            )}`
          );
        }
      } else {
        setPhoneVerificationError(
          "Invalid verification code or another error occurred."
        );
        console.error("Error verifying phone number:", error);
      }
    }
  };

  const handleResetPassword = () => {
    if (!resetEmail) {
      setResetEmailError("Please enter your email.");
      return;
    }

    sendPasswordResetEmail(auth, resetEmail)
      .then(() => {
        setResetEmailSent(true);
        setResetEmailError("");
        setShowResetModal(false);
        alert(
          "Password reset instructions have been successfully sent to your email address. Please check your inbox."
        );
      })
      .catch((error) => {
        console.error("Error resetting password:", error);
        setResetEmailError("Failed to send reset email. Please try again.");
        alert("Failed to send password reset email. Please try again later.");
      });
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__brand">
          <div className="brand-logo">
            <img src={WGLogoLink} alt="Watch Guardian logo" />
          </div>
          <div className="brand-copy">
            <p className="brand-eyebrow">Welcome back</p>
            <h1 className="brand-title">Stay on top of every heartbeat.</h1>
            <p className="brand-description">
              Monitor your teams, respond to alerts, and keep every Watch
              Guardian device running smoothly.
            </p>
          </div>
        </div>

        <div className="login-card__form">
          <div className="form-header">
            <h2>Sign in to continue</h2>
            <p>
              Enter your credentials to access live monitoring and operational
              tools.
            </p>
          </div>

          <form className="login-form" onSubmit={handleLogin}>
            <label className="input-label" htmlFor="email">
              Email
              <div className="input-wrapper">
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </label>

            <label className="input-label" htmlFor="password">
              Password
              <div className="input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </button>
              </div>
            </label>

            <div className="form-footer">
              <button className="submit-button" type="submit" disabled={loginLoading}>
                {loginLoading ? "Signing in..." : "Sign in"}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => setShowResetModal(true)}
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <p className="error-text">Invalid credentials. Please try again.</p>
            )}
          </form>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>Reset your password</h2>
            <p>
              Enter the email on your account and we will send reset
              instructions to it.
            </p>
            <input
              type="email"
              placeholder="you@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            {resetEmailError && (
              <span className="error-text">{resetEmailError}</span>
            )}
            <button className="button1" onClick={handleResetPassword}>
              Send reset instructions
            </button>
            <button
              className="button2"
              onClick={() => setShowResetModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Phone Number Verification Modal */}
      {showPhoneVerificationModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>Verify your phone number</h2>
            <p>
              {isPhoneNumberUpdated ? (
                <>
                  Your phone number has been <strong>recently updated</strong>.
                   Please verify it to proceed.
                  <br />A code will be sent to{" "}
                  {phoneNumber
                    ? `*****${phoneNumber.slice(-3)}`
                    : "Phone number not available"}
                  .
                </>
              ) : (
                <>
                  Please Verify your Phone Number to proceed.
                  <br />A code will be sent to{" "}
                  {phoneNumber
                    ? `*****${phoneNumber.slice(-3)}`
                    : "Phone number not available"}
                  .
                </>
              )}
            </p>
            {verificationCodeSent && (
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter the verification code"
                value={phoneVerificationCode}
                onChange={(e) => setPhoneVerificationCode(e.target.value)}
              />
            )}
            {phoneVerificationError && (
              <span className="error-text">{phoneVerificationError}</span>
            )}

            {!verificationCodeSent ? (
              <button className="button1" onClick={handleResendPhoneVerification}>
                Send verification code
              </button>
            ) : (
              <button className="button1" onClick={handlePhoneVerification}>
                Verify phone number
              </button>
            )}

            <button
              className="button2"
              onClick={() => setShowPhoneVerificationModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Password Update Modal */}
      {showPasswordUpdateModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>Update your password</h2>
            <p>
              For your first sign in, please choose a new password to continue.
            </p>

            <div className="field">
              <input
                type={showPassword1 ? "text" : "password"}
                placeholder="New password"
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword1(!showPassword1)}
                aria-label={showPassword1 ? "Hide password" : "Show password"}
              >
                {showPassword1 ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            <div className="field">
              <input
                type={showPassword2 ? "text" : "password"}
                placeholder="Confirm password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword2(!showPassword2)}
                aria-label={showPassword2 ? "Hide password" : "Show password"}
              >
                {showPassword2 ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            <span>
              Must be 8–20 characters and include an uppercase letter, a number
              and a special character (@$!%*?&#).
            </span>

            {error2 && <span className="error-text">{error2}</span>}
            <button
              className="button1"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Updating…" : "Update password"}
            </button>
            <button
              className="button2"
              onClick={() => setShowPasswordUpdateModal(false)}
            >
              Cancel
            </button>
            {success && <span>Password updated successfully.</span>}
          </div>
        </div>
      )}

      {/* Recaptcha Container */}
      <div id="recaptcha-container" style={{ display: "none" }}></div>

      <Dialog
        open={showMobileAppModal}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Access restricted</DialogTitle>
        <DialogContent dividers>
          <p className="wg-subtitle" style={{ marginBottom: "var(--wg-space-4)" }}>
            This account can only sign in through our mobile application. Scan a
            code below to download the <strong>Watch Guardian App</strong>.
          </p>

          <div className="qr-row">
            <a
              href="https://apps.apple.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <QRCodeSVG value="https://apps.apple.com" size={128} />
              <p>App Store</p>
            </a>

            <a
              href="https://play.google.com/store"
              target="_blank"
              rel="noopener noreferrer"
            >
              <QRCodeSVG value="https://play.google.com/store" size={128} />
              <p>Google Play</p>
            </a>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {loginLoading && <LoadingSpinner />}
    </div>
  );
};

export default Login;
